import { Injectable } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { ChargebeePaymentServiceConfig, PaymentMethodUpdateComponent } from "@app/shared/components";
import { ToastService } from "@app/shared/ui";
import { TranslocoService } from "@ngneat/transloco";
import { PaymentMethodGetDto, PaymentService } from "clearline-api";
import { alertApiError, NULL_OBSERVABLE } from "core-kit";
import { BehaviorSubject, Observable } from "rxjs";
import { shareReplay, switchMap, tap } from "rxjs/operators";
import { ConfirmDialogComponent, ConfirmDialogOptions } from "ui-kit";

@Injectable({
  providedIn: "root"
})
export class PaymentMethodManagementService {
  private refreshPaymentMethod$ = new BehaviorSubject<void>(null);

  public paymentMethod$: Observable<PaymentMethodGetDto> = this.refreshPaymentMethod$.pipe(
    switchMap(() => this.paymentService.getPaymentMethodStatus(true)),
    shareReplay(1)
  );

  constructor(
    private dialog: MatDialog,
    private paymentService: PaymentService,
    private toastService: ToastService,
    private transloco: TranslocoService
  ) {}

  updatePaymentMethod(paymentMethod: PaymentMethodGetDto): Observable<unknown> {
    return this.getValidPaymentMethodData(paymentMethod).pipe(
      switchMap((freshPaymentMethod) => {
        const config: ChargebeePaymentServiceConfig = {
          htmlContentContainerId: "payment-component",
          paymentMethodData: freshPaymentMethod
        };

        return this.dialog
          .open(PaymentMethodUpdateComponent, { data: config, disableClose: true, panelClass: "modal-standard" })
          .afterClosed();
      }),
      alertApiError(this.toastService),
      tap((isSuccess: unknown) => {
        if (!!isSuccess) {
          this.toastService.success(this.transloco.translate("common.topBar.addCreditCardSuccess"));
          this.refreshPaymentMethodData();
        }
      })
    );
  }

  // Always fetch a fresh paymentIntent — Chargebee invalidates intents on its own schedule, so any cached value risks a 400 "Intent status is invalid".
  getValidPaymentMethodData(_current: PaymentMethodGetDto): Observable<PaymentMethodGetDto> {
    return this.paymentService.getPaymentMethodStatus(true);
  }

  deletePaymentMethod(paymentMethod: PaymentMethodGetDto): Observable<any> {
    if (!paymentMethod.hasPaymentSource) {
      return NULL_OBSERVABLE;
    }

    const options: ConfirmDialogOptions = {
      title: this.transloco.translate(`common.topBar.deleteCreditCardConfirmTitle`),
      description: this.transloco.translate(`common.topBar.deleteCreditCardConfirmDescription`),
      cancelBtnLabel: this.transloco.translate(`common.controls.cancel`),
      confirmBtnLabel: this.transloco.translate(`common.controls.confirm`)
    };

    return this.dialog
      .open(ConfirmDialogComponent, {
        panelClass: "modal-standard-s",
        disableClose: true,
        data: options
      })
      .afterClosed()
      .pipe(
        switchMap((isConfirmed) => {
          if (isConfirmed) {
            return this.paymentService.deletePaymentMethod().pipe(
              alertApiError(this.toastService),
              tap(() => {
                this.toastService.success(this.transloco.translate("common.topBar.deleteCreditCardSuccess"));
                this.refreshPaymentMethodData();
              })
            );
          }
          return NULL_OBSERVABLE;
        })
      );
  }

  addPaymentMethod(paymentMethod: PaymentMethodGetDto): Observable<unknown> {
    if (!paymentMethod.hasPaymentSource) {
      return this.updatePaymentMethod(paymentMethod);
    }
    return NULL_OBSERVABLE;
  }

  refreshPaymentMethodData(): void {
    this.refreshPaymentMethod$.next(null);
  }
}
